import type { AttorneyGeneralWizardDraft } from '../types/attorneyGeneralCaseRequest';
import type { CivilAdviseWizardDraft } from '../types/civilAdviseCaseRequest';
import type { CivilAppellateWizardDraft } from '../types/civilAppellateCaseRequest';
import type { CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import type { CriminalAppellateWizardDraft } from '../types/criminalAppellateCaseRequest';
import type { CriminalWizardDraft } from '../types/criminalCaseRequest';
import type { ExtraditionMlaWizardDraft } from '../types/extraditionMlaCaseRequest';
import type { NominationWizardDraft } from '../types/nominationCaseRequest';
import type { PublicPetitionWizardDraft } from '../types/publicPetitionCaseRequest';
import type { RightToInformationWizardDraft } from '../types/rightToInformationCaseRequest';
import { validateAttorneyGeneralStep } from '../pages/wizard/attorneyGeneral/attorneyGeneralStepValidation';
import { validateCivilAdviseStep } from '../pages/wizard/civilAdvise/civilAdviseStepValidation';
import { validateCivilAppellateStep } from '../pages/wizard/civilAppellate/civilAppellateStepValidation';
import { validateCivilLitigationStep } from '../pages/wizard/civilLitigation/civilLitigationStepValidation';
import { validateStep5Documents as validateCriminalStep5Documents } from '../pages/wizard/criminal/criminalStepValidation';
import { validateCriminalAppellateStep } from '../pages/wizard/criminalAppellate/criminalAppellateStepValidation';
import { validateExtraditionMlaStep } from '../pages/wizard/extraditionMla/extraditionMlaStepValidation';
import { validateNominationStep } from '../pages/wizard/nomination/nominationStepValidation';
import { validatePublicPetitionStep } from '../pages/wizard/publicPetition/publicPetitionStepValidation';
import { validateRightToInformationStep } from '../pages/wizard/rightToInformation/rightToInformationStepValidation';
import { wizardStepPath, type WizardFlowKind } from './wizardCategoryRoute';

/** Max wizard step number per flow (upload-documents / last data step — not review). */
export const WIZARD_FLOW_MAX_STEP: Record<Exclude<WizardFlowKind, 'unknown'>, number> = {
  criminal: 5,
  'criminal-appellate': 5,
  'civil-advise': 4,
  'civil-litigation': 5,
  'civil-appellate': 5,
  'extradition-mla': 6,
  'nomination-requests': 4,
  'public-petition': 5,
  'right-to-information': 4,
  'attorney-general': 12,
};

/**
 * Criminal has no shared validateCriminalStep — category switches clear sub-category
 * (step 1) and documents (step 5). Other steps are treated as valid for this check.
 */
function isCriminalStepValid(step: number, draft: CriminalWizardDraft): boolean {
  if (step >= 1) {
    if (!(draft.fileCategoryId ?? '').trim()) return false;
    if (!(draft.subFileCategoryId ?? '').trim()) return false;
  }
  if (step >= 5) {
    const errors = validateCriminalStep5Documents(draft);
    if (Object.keys(errors).length > 0) return false;
  }
  return true;
}

function isFlowStepValid(flow: WizardFlowKind, step: number, draft: unknown): boolean {
  switch (flow) {
    case 'criminal':
      return isCriminalStepValid(step, draft as CriminalWizardDraft);
    case 'criminal-appellate':
      return validateCriminalAppellateStep(
        step as 1 | 2 | 3 | 4 | 5,
        draft as CriminalAppellateWizardDraft,
      ).valid;
    case 'civil-advise':
      return validateCivilAdviseStep(
        step as 1 | 2 | 3 | 4,
        draft as CivilAdviseWizardDraft,
      ).valid;
    case 'civil-litigation':
      return validateCivilLitigationStep(
        step as 1 | 2 | 3 | 4 | 5,
        draft as CivilLitigationWizardDraft,
      ).valid;
    case 'civil-appellate':
      return validateCivilAppellateStep(
        step as 1 | 2 | 3 | 4 | 5,
        draft as CivilAppellateWizardDraft,
      ).valid;
    case 'extradition-mla':
      return validateExtraditionMlaStep(
        step as 1 | 2 | 3 | 4 | 5 | 6,
        draft as ExtraditionMlaWizardDraft,
      ).valid;
    case 'nomination-requests':
      return validateNominationStep(
        step as 1 | 2 | 3 | 4,
        draft as NominationWizardDraft,
      ).valid;
    case 'public-petition':
      return validatePublicPetitionStep(
        step as 1 | 2 | 3 | 4 | 5,
        draft as PublicPetitionWizardDraft,
      ).valid;
    case 'right-to-information':
      return validateRightToInformationStep(
        step as 1 | 2 | 3 | 4,
        draft as RightToInformationWizardDraft,
      ).valid;
    case 'attorney-general':
      return validateAttorneyGeneralStep(
        step as
          | 1
          | 2
          | 3
          | 4
          | 5
          | 6
          | 7
          | 8
          | 9
          | 10
          | 11
          | 12,
        draft as AttorneyGeneralWizardDraft,
      ).valid;
    default:
      return true;
  }
}

/**
 * After a file-category change, find the earliest wizard step with unfilled mandatory
 * fields for the new category. Does not read or write API `current_step`.
 * Returns null when every step is valid (caller may leave the user on the current route).
 */
export function findFirstInvalidWizardStep(
  flow: WizardFlowKind,
  draft: unknown,
): number | null {
  if (flow === 'unknown') return 1;
  const maxStep = WIZARD_FLOW_MAX_STEP[flow];
  for (let step = 1; step <= maxStep; step += 1) {
    if (!isFlowStepValid(flow, step, draft)) return step;
  }
  return null;
}

/** Build the wizard step path for a flow (all intranet flows share `/requests/new/step-N`). */
export function wizardPathForStep(step: number, draftRequestId?: string): string {
  return wizardStepPath('', step, draftRequestId);
}
